import React, { useCallback, useEffect, useState } from 'react';
import { AlertBanner } from '@ygtechnologies/rubus-ui';
import { Link } from 'react-router-dom';
import { FlatCard } from '@/components/FlatCard';
import { getLiveStatistics, LiveItem, LiveStatistics } from '@/services/statistics';
import { dummyLiveStatistics } from './statisticsDummyData';
import shared from './SystemHealthPage.module.scss';
import theme from './StatisticsTab.module.scss';

const POLL_MS = 30_000;

const fmt = (n: number) => n.toLocaleString('en-GB');

function timeAgo(iso: string, now: number) {
    const m = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    return `${Math.floor(m / 1440)}d ago`;
}

const initials = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join('') || '?';

const StatisticsTab: React.FC<{ now: number }> = ({ now }) => {
    const [live, setLive] = useState<LiveStatistics | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(
        () =>
            getLiveStatistics()
                .then((s) => {
                    setLive(s);
                    setError(null);
                })
                .catch((e: Response) => setError(`Could not load statistics (${e?.status ?? 'network error'}).`)),
        []
    );
    useEffect(() => {
        load();
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [load]);

    // Last good answer wins; dummy data only until the endpoint has answered once.
    const s = live ?? dummyLiveStatistics;
    const dummy = !live;
    const badge = dummy ? <span className={shared.dummyBadge}>Dummy</span> : undefined;
    const newestUser = s.latestUsers[0];
    const newestProject = s.latestProjects[0];

    return (
        <div className={theme.tab}>
            {error && dummy && (
                <AlertBanner
                    type="warning"
                    title="Showing example numbers until the statistics endpoint answers"
                    actions={[{ text: 'Try again', onClick: load }]}>
                    {error}
                </AlertBanner>
            )}

            <section className={shared.stats}>
                <div className={shared.stat}>
                    <div className={shared.statLabel}>
                        <span className={`${shared.dot} ${shared.ok} ${shared.pulse}`} /> Active now {badge}
                    </div>
                    <div className={shared.statValue}>{fmt(s.activeNow)}</div>
                    <div className={shared.muted}>visited in the last 15 min</div>
                </div>
                <Tile label="Active last 24h" value={fmt(s.activeLast24Hours)} note="distinct users" badge={badge} />
                <Tile
                    label="Users"
                    value={fmt(s.totalUsers)}
                    note={`+${fmt(s.newUsersToday)} today · +${fmt(s.newUsersLast7Days)} this week`}
                    badge={badge}
                />
                <Tile
                    label="Projects"
                    value={fmt(s.totalProjects)}
                    note={`+${fmt(s.newProjectsLast30Days)} last 30 days`}
                    badge={badge}
                />
                <Tile
                    label="Newest user"
                    value={newestUser?.name || '—'}
                    note={newestUser && `joined ${timeAgo(newestUser.at, now)}`}
                    badge={badge}
                    small
                />
                <Tile
                    label="Newest project"
                    value={newestProject?.name || '—'}
                    note={newestProject && `${newestProject.detail ?? ''} · ${timeAgo(newestProject.at, now)}`}
                    badge={badge}
                    small
                />
            </section>

            <FlatCard title="Active users per hour" actions={badge}>
                <HourBars values={s.activeUsersPerHour} now={now} />
            </FlatCard>

            <div className={theme.lists}>
                <FlatCard title="Recently active" actions={badge}>
                    <PeopleList items={s.recentlyActive} now={now} to={dummy ? undefined : (i) => `/users/${i.id}`} />
                </FlatCard>
                <FlatCard title="Newest users" actions={badge}>
                    <PeopleList items={s.latestUsers} now={now} to={dummy ? undefined : (i) => `/users/${i.id}`} />
                </FlatCard>
                <FlatCard title="Newest projects" actions={badge}>
                    <ItemList items={s.latestProjects} now={now} to={dummy ? undefined : (i) => `/projects/${i.id}`} />
                </FlatCard>
                <FlatCard title="Newest profiles" actions={badge}>
                    <ItemList items={s.latestProfiles} now={now} to={dummy ? undefined : (i) => `/profiles/${i.id}`} />
                </FlatCard>
            </div>
        </div>
    );
};

const Tile: React.FC<{
    label: string;
    value: React.ReactNode;
    note?: React.ReactNode;
    badge?: React.ReactNode;
    small?: boolean;
}> = ({ label, value, note, badge, small }) => (
    <div className={shared.stat}>
        <div className={shared.statLabel}>
            {label} {badge}
        </div>
        <div className={`${shared.statValue} ${small ? theme.smallValue : ''}`} title={String(value)}>
            {value}
        </div>
        {note && <div className={shared.muted}>{note}</div>}
    </div>
);

/** Optional link target; dummy rows don't link anywhere. */
type ToFn = ((item: LiveItem) => string) | undefined;

const Name: React.FC<{ item: LiveItem; to: ToFn }> = ({ item, to }) =>
    to ? (
        <Link to={to(item)} className={theme.name}>
            {item.name || '(no name)'}
        </Link>
    ) : (
        <span className={theme.name}>{item.name || '(no name)'}</span>
    );

const PeopleList: React.FC<{ items: LiveItem[]; now: number; to: ToFn }> = ({ items, now, to }) =>
    items.length === 0 ? (
        <p className={shared.muted}>Nobody yet.</p>
    ) : (
        <ul className={theme.list}>
            {items.map((i) => (
                <li key={`${i.id}-${i.at}`} className={theme.row}>
                    <span className={theme.avatar} aria-hidden>
                        {initials(i.name)}
                    </span>
                    <div className={theme.body}>
                        <Name item={i} to={to} />
                        {i.detail && <div className={`${shared.muted} ${theme.detail}`}>{i.detail}</div>}
                    </div>
                    <time className={shared.muted}>{timeAgo(i.at, now)}</time>
                </li>
            ))}
        </ul>
    );

const ItemList: React.FC<{ items: LiveItem[]; now: number; to: ToFn }> = ({ items, now, to }) =>
    items.length === 0 ? (
        <p className={shared.muted}>Nothing yet.</p>
    ) : (
        <ul className={theme.list}>
            {items.map((i) => (
                <li key={i.id} className={theme.row}>
                    <div className={theme.body}>
                        <Name item={i} to={to} />
                        {i.detail && <div className={`${shared.muted} ${theme.detail}`}>{i.detail}</div>}
                    </div>
                    <time className={shared.muted}>{timeAgo(i.at, now)}</time>
                </li>
            ))}
        </ul>
    );

const HourBars: React.FC<{ values: number[]; now: number }> = ({ values, now }) => {
    const max = Math.max(1, ...values);
    const peak = values.indexOf(Math.max(...values));
    // Bucket i covers the hour ending (23 - i) hours ago.
    const hourLabel = (i: number) =>
        new Date(now - (values.length - 1 - i) * 3_600_000).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
        });
    return (
        <div className={theme.hours}>
            <svg viewBox={`0 0 ${values.length * 10} 60`} preserveAspectRatio="none" className={theme.bars}>
                {values.map((v, i) => {
                    const h = Math.max(1, (v / max) * 58);
                    const cls = i === peak ? theme.peak : i === values.length - 1 ? theme.current : theme.bar;
                    return (
                        <rect key={i} x={i * 10 + 1} y={60 - h} width={8} height={h} rx={1.5} className={cls}>
                            <title>{`${v} active users in the hour up to ${hourLabel(i)}`}</title>
                        </rect>
                    );
                })}
            </svg>
            <div className={`${theme.axis} ${shared.muted}`}>
                <span>24h ago</span>
                <span>
                    peak {values[peak]} at {hourLabel(peak)}
                </span>
                <span>now</span>
            </div>
        </div>
    );
};

export default StatisticsTab;
